/**
 * Node addressing over a compiled (or resolved) document.
 *
 * An address names a node as `instanceId` [+ `#sectionRef`] [+ `/path`]:
 *
 *   rent                         the whole block instance
 *   rent#hyra                    section "hyra" inside instance "rent"
 *   rent#hyra/2                  3rd node of that section's content
 *   rent#hyra/1/items/0/content/0  …inside a list item
 *   rent/0                       1st top-level node of the instance
 *
 * Parts of a node (ADR-0102 addendum 3) extend a node's address with a
 * trailing step that `walkPath` never resolves on its own:
 *
 *   rent#hyra/1/items/2          3rd row of a detail list / 3rd option's text
 *   rent#hyra/1/columns/0/header 1st column header of a table
 *   rent#hyra/1/caption          a table's caption
 *
 * Components are URI-encoded, so any instance id or ref round-trips. The
 * canonical address of a node uses its NEAREST enclosing section within the
 * instance, which keeps paths short and survives content shifting in other
 * sections.
 */
import { fingerprint, fingerprintInstance, fingerprintPart } from './fingerprint.js';
const CHILD_KEYS = new Set(['content', 'items', 'rows', 'cells', 'children', 'description']);
const pathSuffix = (path) => (path ?? []).map((seg) => `/${encodeURIComponent(String(seg))}`).join('');
/**
 * Canonical string form of an anchor. Base nodes: `instance[#section][/path]`;
 * output of an earlier patch: `@patchId[/path]` (`@5/0` = the first
 * paragraph of the clause log entry 5 inserted). `@` never survives
 * `encodeURIComponent`, so the two forms cannot collide.
 */
export function formatNodeAddress(anchor) {
    if ('fromPatch' in anchor)
        return `@${encodeURIComponent(anchor.fromPatch)}${pathSuffix(anchor.path)}`;
    let out = encodeURIComponent(anchor.instanceId);
    if (anchor.sectionId != null)
        out += `#${encodeURIComponent(anchor.sectionId)}`;
    return out + pathSuffix(anchor.path);
}
/** Address of a log anchor (`fromSeq` → `@seq`). */
export const formatLogAddress = (anchor) => formatNodeAddress(toPatchAnchor(anchor));
/** Log anchor → engine anchor (`fromSeq: 5` → `fromPatch: '5'`). */
export const toPatchAnchor = (anchor) => 'fromSeq' in anchor ? { fromPatch: String(anchor.fromSeq), ...(anchor.path?.length ? { path: anchor.path } : {}) } : anchor;
const parsePath = (segs) => segs.map((s) => {
    const decoded = decodeURIComponent(s);
    return /^\d+$/.test(decoded) ? Number(decoded) : decoded;
});
export function parseNodeAddress(address) {
    try {
        const [head, ...segs] = address.split('/');
        if (!head)
            return null;
        const path = parsePath(segs);
        if (head.startsWith('@')) {
            const fromPatch = decodeURIComponent(head.slice(1));
            if (!fromPatch)
                return null;
            return { fromPatch, ...(path.length ? { path } : {}) };
        }
        const hash = head.indexOf('#');
        const instanceId = decodeURIComponent(hash === -1 ? head : head.slice(0, hash));
        if (!instanceId)
            return null;
        const sectionId = hash === -1 ? undefined : decodeURIComponent(head.slice(hash + 1));
        return { instanceId, ...(sectionId != null ? { sectionId } : {}), ...(path.length ? { path } : {}) };
    }
    catch {
        return null;
    }
}
/**
 * The log anchor for a rendered `data-node-address` — what the review panel
 * puts in a new entry. `@5/0` → `{ fromSeq: 5, path: [0] }`.
 */
export function logAnchorFromAddress(address) {
    const parsed = parseNodeAddress(address);
    if (!parsed)
        return null;
    if (!('fromPatch' in parsed))
        return parsed;
    if (!/^[1-9]\d*$/.test(parsed.fromPatch))
        return null;
    return { fromSeq: Number(parsed.fromPatch), ...(parsed.path ? { path: parsed.path } : {}) };
}
/** Child block lists of a node, with the property steps that reach them. */
export function childLists(node) {
    switch (node.type) {
        case 'section':
        case 'notice':
        case 'group':
            return [{ segs: ['content'], list: node.content }];
        case 'list':
            return node.items.map((item, i) => ({ segs: ['items', i, 'content'], list: item.content }));
        case 'optionList':
            return node.items.flatMap((item, i) => (item.children ? [{ segs: ['items', i, 'children'], list: item.children }] : []));
        case 'table':
            return node.rows.flatMap((row, r) => row.cells.map((cell, c) => ({ segs: ['rows', r, 'cells', c, 'content'], list: cell.content })));
        case 'detailList':
            return node.items.map((item, i) => ({ segs: ['items', i, 'description'], list: item.description }));
        default:
            return [];
    }
}
/** Every block instance's top-level output, keyed by instance id. */
export function instanceRoots(document) {
    const roots = new Map();
    for (const part of document.parts) {
        const regions = [
            ['letterhead', part.letterhead],
            ['body', part.body],
            ['footer', part.footer],
        ];
        for (const [region, list] of regions) {
            for (const node of list ?? []) {
                if (!node.instanceId)
                    continue;
                let root = roots.get(node.instanceId);
                if (!root) {
                    root = { instanceId: node.instanceId, part: part.key, region, nodes: [] };
                    roots.set(node.instanceId, root);
                }
                root.nodes.push(node);
            }
        }
    }
    return roots;
}
const isBlockNode = (value) => value != null && typeof value === 'object' && typeof value.type === 'string';
function findSection(nodes, ref) {
    for (const node of nodes) {
        if (node.type === 'section' && node.ref === ref)
            return node;
        for (const { list } of childLists(node)) {
            const hit = findSection(list, ref);
            if (hit)
                return hit;
        }
    }
    return null;
}
export function walkPath(rootList, path) {
    const [first, ...rest] = path;
    if (typeof first !== 'number')
        return null;
    let current = rootList[first];
    for (const seg of rest) {
        if (current == null || typeof current !== 'object')
            return null;
        if (typeof seg === 'number') {
            if (!Array.isArray(current))
                return null;
            current = current[seg];
        }
        else {
            if (!CHILD_KEYS.has(seg) || Array.isArray(current))
                return null;
            current = current[seg];
        }
    }
    return isBlockNode(current) ? current : null;
}
/** Resolves an anchor against a document. `null` = anchor missing. */
export function resolveAnchor(document, anchor, roots = instanceRoots(document)) {
    const root = roots.get(anchor.instanceId);
    if (!root)
        return null;
    const path = anchor.path ?? [];
    if (anchor.sectionId == null) {
        if (path.length === 0) {
            return { kind: 'instance', instanceId: root.instanceId, root, fingerprint: fingerprintInstance(root.nodes) };
        }
        const node = walkPath(root.nodes, path);
        return node ? { kind: 'node', instanceId: root.instanceId, root, node, fingerprint: fingerprint(node) } : null;
    }
    const section = findSection(root.nodes, anchor.sectionId);
    if (!section || section.type !== 'section')
        return null;
    const node = path.length === 0 ? section : walkPath(section.content, path);
    return node ? { kind: 'node', instanceId: root.instanceId, root, node, fingerprint: fingerprint(node) } : null;
}
/** Whether a node is locked by itself (its own `locked` flag, or a signature) — ignoring ancestors. */
export const isLockedNode = (node) => node.locked === true || node.type === 'signature';
/** Whether any of the nodes, or anything inside them, is locked. */
export const containsLocked = (nodes) => nodes.some((n) => isLockedNode(n) || childLists(n).some(({ list }) => containsLocked(list)));
/**
 * Every node that is EFFECTIVELY locked — locked itself or inside a locked
 * ancestor — in the given node lists.
 */
export function collectLockedNodes(lists, out = new Set()) {
    const visit = (list, inherited) => {
        for (const node of list) {
            const locked = inherited || isLockedNode(node);
            if (locked)
                out.add(node);
            for (const { list: child } of childLists(node))
                visit(child, locked);
        }
    };
    for (const list of lists)
        visit(list, false);
    return out;
}
/** Whether a node is a legal `replaceText` target. */
export const isTextTarget = (node) => node.type === 'paragraph' || node.type === 'heading' || (node.type === 'section' && node.title != null);
/**
 * Whether a node (or instance output) may be removed: nothing in it is
 * locked. Signatures never may — the signing plan depends on them. Ancestor
 * locks are not visible here; see `AddressableNode.locked` / `edit.locked`.
 */
export const isRemovable = (nodes) => !containsLocked(nodes);
/**
 * Every addressable node of a compiled document, canonical addresses, in
 * document order (instance root first, then its nodes depth-first). The
 * review panel's index from a DOM `data-node-address` to a patch anchor.
 */
export function listAddressableNodes(document) {
    const planes = new Map(document.manifest.map((m) => [m.instanceId, m.plane]));
    const out = [];
    for (const root of instanceRoots(document).values()) {
        const plane = planes.get(root.instanceId);
        const base = { instanceId: root.instanceId, ...(plane ? { plane } : {}), part: root.part, region: root.region };
        const instanceLocked = root.nodes.length > 0 && root.nodes.every(isLockedNode);
        const instanceContains = containsLocked(root.nodes);
        out.push({
            ...base,
            address: formatNodeAddress({ instanceId: root.instanceId }),
            anchor: { instanceId: root.instanceId },
            fingerprint: fingerprintInstance(root.nodes),
            nodeType: 'instance',
            locked: instanceLocked,
            containsLocked: instanceContains,
            ops: { replaceText: false, remove: !instanceContains, insertAround: true, append: false, move: false },
        });
        const visit = (list, sectionId, prefix, parentLocked) => {
            list.forEach((node, index) => {
                const locked = parentLocked || isLockedNode(node);
                const inside = containsLocked([node]);
                const ownSection = node.type === 'section';
                const anchorPos = ownSection
                    ? { instanceId: root.instanceId, sectionId: node.ref }
                    : {
                        instanceId: root.instanceId,
                        ...(sectionId != null ? { sectionId } : {}),
                        path: [...prefix, index],
                    };
                out.push({
                    ...base,
                    address: formatNodeAddress(anchorPos),
                    anchor: anchorPos,
                    fingerprint: fingerprint(node),
                    nodeType: node.type,
                    locked,
                    containsLocked: inside,
                    ops: {
                        replaceText: !locked && isTextTarget(node),
                        remove: !locked && !inside,
                        insertAround: !parentLocked,
                        append: !locked && (node.type === 'section' || node.type === 'group' || node.type === 'notice'),
                        move: !locked && !inside && node.type === 'section',
                    },
                });
                if (ownSection) {
                    visit(node.content, node.ref, [], locked);
                }
                else {
                    for (const { segs, list: children } of childLists(node))
                        visit(children, sectionId, [...prefix, index, ...segs], locked);
                }
            });
        };
        visit(root.nodes, undefined, [], false);
    }
    return out;
}
/** The base anchor + compiled fingerprint at an address (a node or a part), or `null` if it does not resolve. */
export function anchorAt(document, address) {
    const parsed = parseNodeAddress(address);
    if (!parsed || 'fromPatch' in parsed)
        return null;
    const target = resolveAnchor(document, parsed) ?? resolvePartAnchor(document, parsed);
    return target ? { anchor: parsed, fingerprint: target.fingerprint } : null;
}
/**
 * Splits a path into the owner node's path and the part step at its end
 * (`…/items/<i>`, `…/columns/<i>/header`, `…/caption`). Which `items/<i>`
 * means — a row or an option — depends on the owner's type.
 */
export function splitPartPath(path) {
    const n = path.length;
    if (n >= 2 && path[n - 1] === 'caption')
        return { ownerPath: path.slice(0, -1), step: 'caption' };
    if (n >= 4 && path[n - 1] === 'header' && path[n - 3] === 'columns' && typeof path[n - 2] === 'number') {
        return { ownerPath: path.slice(0, -3), step: 'columnHeader', index: path[n - 2] };
    }
    if (n >= 3 && path[n - 2] === 'items' && typeof path[n - 1] === 'number') {
        return { ownerPath: path.slice(0, -2), step: 'items', index: path[n - 1] };
    }
    return null;
}
/** The part a split path names on a given owner node, or `null` when the owner has no such part. */
export function partOf(owner, split) {
    const index = split.index ?? 0;
    switch (split.step) {
        case 'caption':
            return owner.type === 'table' && owner.caption != null ? { kind: 'caption' } : null;
        case 'columnHeader':
            return owner.type === 'table' && owner.columns[index] ? { kind: 'columnHeader', index } : null;
        case 'items':
            if (owner.type === 'detailList' && owner.items[index])
                return { kind: 'row', index };
            if (owner.type === 'optionList' && owner.items[index])
                return { kind: 'option', index };
            return null;
    }
}
/** Path steps from an owner node to one of its parts. */
export const partSegs = (part) => part.kind === 'caption' ? ['caption'] : part.kind === 'columnHeader' ? ['columns', part.index, 'header'] : ['items', part.index];
/** Resolves a part anchor (`…/items/<i>`, `…/columns/<i>/header`, `…/caption`). `null` = not a part / missing. */
export function resolvePartAnchor(document, anchor, roots = instanceRoots(document)) {
    const split = anchor.path ? splitPartPath(anchor.path) : null;
    if (!split)
        return null;
    const owner = resolveAnchor(document, { ...anchor, path: split.ownerPath }, roots);
    if (!owner || owner.kind !== 'node')
        return null;
    const part = partOf(owner.node, split);
    if (!part)
        return null;
    const fp = fingerprintPart(owner.node, part);
    if (fp == null)
        return null;
    return { kind: 'part', instanceId: owner.instanceId, root: owner.root, owner: owner.node, part, fingerprint: fp };
}
/**
 * Every addressable part of a compiled document, in document order — the
 * rows, option texts, column headers and captions of every detail list,
 * option list and table that `listAddressableNodes` lists.
 */
export function listAddressableParts(document) {
    const out = [];
    const nodes = listAddressableNodes(document);
    const roots = instanceRoots(document);
    for (const entry of nodes) {
        if (entry.nodeType !== 'detailList' && entry.nodeType !== 'optionList' && entry.nodeType !== 'table')
            continue;
        const target = resolveAnchor(document, entry.anchor, roots);
        if (!target || target.kind !== 'node')
            continue;
        const owner = target.node;
        const parts = owner.type === 'detailList'
            ? owner.items.map((_, index) => ({ kind: 'row', index }))
            : owner.type === 'optionList'
                ? owner.items.map((_, index) => ({ kind: 'option', index }))
                : owner.type === 'table'
                    ? [
                        ...owner.columns.flatMap((c, index) => (c.header ? [{ kind: 'columnHeader', index }] : [])),
                        ...(owner.caption ? [{ kind: 'caption' }] : []),
                    ]
                    : [];
        for (const part of parts) {
            const fp = fingerprintPart(owner, part);
            if (fp == null)
                continue;
            const anchor = { ...entry.anchor, path: [...(entry.anchor.path ?? []), ...partSegs(part)] };
            const rowLocked = part.kind === 'row' && owner.type === 'detailList' && containsLocked(owner.items[part.index]?.description ?? []);
            const locked = entry.locked || rowLocked;
            const row = part.kind === 'row';
            out.push({
                address: formatNodeAddress(anchor),
                anchor,
                fingerprint: fp,
                part,
                ownerAddress: entry.address,
                ownerType: entry.nodeType,
                instanceId: entry.instanceId,
                locked,
                ops: {
                    editRow: row && !locked,
                    removeRow: row && !locked,
                    insertRow: row && !entry.locked,
                    editLabel: !row && !locked,
                },
            });
        }
    }
    return out;
}
//# sourceMappingURL=address.js.map